import {
	Popover,
	PopoverAnchor,
	PopoverContent,
} from '@qr-front/ui/dashboard/components/Popover'
import { subExpiredDialogAtom } from 'features/library/client'
import { useScrollLock } from 'hooks/useScrollLock'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { UserStatusEnum } from 'types/subscription'

import { userAtom } from '@/global-state'
import { useRouter } from '@/i18n-routing'
import { getTrialRemainingTime } from '@/utils'

import {
	desktopTableRowActionPopoverAtom,
	desktopTableRowActionPopoverTypeAtom,
	desktopTableRowPopoverStateAtom,
	qrItemState,
} from '../dialogsState'
import {
	downloadActions,
	moreActionsDesktop,
	TABLE_ACTION_TYPE,
	TABLE_ROW_ACTION_TYPE,
	TAction,
	TActionOption,
} from '../TableActions/Actions/actionOptions'
import { ActionOptionsContainer } from '../TableActions/Actions/ActionOptionsContainer'

export const DesktopActionsDialog = () => {
	const router = useRouter()

	const qrItem = useAtomValue(qrItemState)
	const [isOpen, setIsOpen] = useAtom(desktopTableRowActionPopoverAtom)
	const actionType = useAtomValue(desktopTableRowActionPopoverTypeAtom)

	const popoverState = useAtomValue(desktopTableRowPopoverStateAtom)
	const { buttonRef } = popoverState

	useScrollLock({ isOpen })

	const [anchorPosition, setAnchorPosition] = useState({
		top: 0,
		left: 0,
		width: 0,
		height: 0,
	})

	useEffect(() => {
		if (buttonRef?.current && isOpen) {
			const rect = buttonRef.current.getBoundingClientRect()
			setAnchorPosition({
				top: rect.top,
				left: rect.left,
				width: rect.width,
				height: rect.height,
			})
		}
	}, [buttonRef, isOpen])

	const setOpenExpiredTrialDialog = useSetAtom(subExpiredDialogAtom)

	const user = useAtomValue(userAtom)
	const userTrial = getTrialRemainingTime(
		user?.createdAt,
		user?.flowData?.freeDaysQuota,
	)
	const showExpiredDialog =
		userTrial.expired &&
		[
			UserStatusEnum.FREEMIUM_OLD,
			UserStatusEnum.PREMIUM_CANCELLED,
			UserStatusEnum.PREMIUM_IN_DEBT,
			UserStatusEnum.DEACTIVATED,
		].includes(user?.status as UserStatusEnum)

	if (!qrItem || !isOpen) {
		return null
	}

	const handleOptionAction = (key: TActionOption['key'], action: TAction) => {
		if (
			showExpiredDialog &&
			key !== TABLE_ACTION_TYPE.DownloadAsPng &&
			key !== TABLE_ACTION_TYPE.DownloadAsJpg &&
			key !== TABLE_ACTION_TYPE.DownloadAsSvg &&
			key !== TABLE_ACTION_TYPE.Share &&
			key !== TABLE_ACTION_TYPE.Delete
		) {
			setOpenExpiredTrialDialog(true)
			setIsOpen(false)
			return
		}
		if (key === TABLE_ACTION_TYPE.Delete) {
			action(qrItem)
			setIsOpen(false)
			return
		}
		action(qrItem)
		setIsOpen(false)
	}

	const isDownloadActionType = actionType === TABLE_ROW_ACTION_TYPE.Download
	const popoverActions = isDownloadActionType
		? downloadActions
		: moreActionsDesktop({ router, qrItem })
	const popoverCls = isDownloadActionType
		? 'w-[177px] max-w-[177px]'
		: 'w-fit max-w-fit'

	return (
		<>
			{isOpen && buttonRef?.current && (
				<Popover open={isOpen} onOpenChange={setIsOpen}>
					<PopoverAnchor asChild>
						<div
							style={{
								position: 'fixed',
								top: anchorPosition.top,
								left: anchorPosition.left,
								width: anchorPosition.width,
								height: anchorPosition.height,
								pointerEvents: 'none',
							}}
						/>
					</PopoverAnchor>

					<PopoverContent
						side="bottom"
						align="end"
						sideOffset={5}
						className={popoverCls}
					>
						<ActionOptionsContainer
							options={popoverActions}
							handleOptionAction={handleOptionAction}
						/>
					</PopoverContent>
				</Popover>
			)}
		</>
	)
}
